<template>
  <!-- A path: the steps something goes through, in order, on a dark band. When it scrolls into
       view the line draws through the steps, each step lights up as the line reaches it, and a
       pulse runs along the line twice. Each step can carry the app's own label for it (`chip`,
       kept in English like other product strings) and a side branch (`branch`). Resting styles
       are the final frame (no JS, crawlers, reduce-motion). Desktop runs left to right; phones
       run top to bottom. Without `steps` it is a short bridge band: heading, intro and the
       `path` + `linkLabel` link to the page that tells the rest. -->
  <section class="spt" :class="{ 'spt--bridge': !chapter.steps?.length }">
    <div class="spt-shell">
      <header class="spt-heading">
        <div>
          <h2>{{ chapter.title }}</h2>
        </div>
        <div>
          <p class="spt-intro">{{ chapter.intro }}</p>
          <NuxtLink v-if="chapter.path" :to="localePath(chapter.path)" class="spt-link">
            {{ chapter.linkLabel }} <span aria-hidden="true">-&gt;</span>
          </NuxtLink>
        </div>
      </header>

      <div v-if="chapter.steps?.length" ref="rootEl" class="spt-map" :class="{ 'is-run': running || armed, 'is-armed': armed }" :style="{ '--n': chapter.steps.length }">
        <span class="spt-line" aria-hidden="true"><i /><b /></span>
        <ol class="spt-steps">
          <li v-for="(step, i) in chapter.steps" :key="step.title" class="spt-step" :style="{ '--d': `${0.2 + i * 0.45}s` }">
            <span class="spt-dot" aria-hidden="true"><svg viewBox="0 0 24 24"><path :d="step.icon" /></svg></span>
            <span class="spt-num">{{ String(i + 1).padStart(2, "0") }}</span>
            <h3>{{ step.title }}</h3>
            <p>{{ step.copy }}</p>
            <span v-if="step.chip" class="spt-chip" translate="no">{{ step.chip }}</span>
            <p v-if="step.branch" class="spt-branch"><b>{{ step.branch.label }}</b> {{ step.branch.copy }}</p>
          </li>
        </ol>
      </div>
      <p v-if="chapter.note" class="spt-note">{{ chapter.note }}</p>
    </div>
  </section>
</template>

<script setup>
defineProps({
  chapter: { type: Object, required: true },
});
const { localePath } = useWorkspaceLocale();
const rootEl = ref(null);
const { running, armed } = useMotionInView(rootEl, { threshold: 0.3 });
</script>

<style scoped>
.spt { background: #0b1220; padding: 6.5rem 0; color: #e2e8f0; }
.spt-shell { width: min(100% - 3rem, 84rem); margin: 0 auto; }
.spt-heading { display: grid; gap: 1.25rem; align-items: end; }
@media (min-width: 1024px) { .spt-heading { grid-template-columns: 1fr 1fr; gap: 2.5rem; } }
.spt-heading h2 { color: #f8fafc; font-size: clamp(2rem, 4vw, 3.25rem); font-weight: 880; line-height: 1.04; text-wrap: balance; }
.spt-intro { color: #cbd5e1; font-size: 1rem; line-height: 1.7; }
.spt-link { display: inline-flex; gap: 0.4rem; margin-top: 1rem; color: #67e8f9; font-weight: 800; }
.spt-link:hover { text-decoration: underline; }
.spt--bridge { padding: 4rem 0; }

.spt-map { position: relative; margin-top: 3.5rem; }
.spt-steps { display: grid; grid-template-columns: repeat(var(--n), minmax(0, 1fr)); gap: 1.25rem; margin: 0; padding: 0; list-style: none; }
/* The line runs from the centre of the first dot to the centre of the last (dots sit at the
   start of each 1.25rem-gapped column). */
.spt-line { position: absolute; top: 21px; left: 22px; right: calc((100% - (var(--n) - 1) * 1.25rem) / var(--n) - 22px); height: 2px; background: #1e293b; }
.spt-line i { position: absolute; inset: 0; background: linear-gradient(90deg, #1ba7ce, #67e8f9); transform-origin: left center; }
.spt-line b { position: absolute; top: -3px; left: 0; width: 100%; height: 8px; opacity: 0; }
.spt-line b::before { content: ""; position: absolute; left: 0; width: 8px; height: 8px; border-radius: 2px; background: #a5f3fc; box-shadow: 0 0 12px #67e8f9; }
.spt-step { position: relative; display: grid; align-content: start; justify-items: start; gap: 0.4rem; }
.spt-dot { position: relative; z-index: 1; display: grid; place-items: center; width: 44px; height: 44px; margin: 0 0 0.5rem; border: 1px solid #1e3a4a; border-radius: 10px; background: #0f2230; color: #67e8f9; }
.spt-dot svg { width: 20px; height: 20px; fill: currentColor; }
.spt-num { color: #94a3b8; font-size: 0.78rem; font-weight: 800; font-variant-numeric: tabular-nums; }
.spt-step h3 { color: #f8fafc; font-size: 1.15rem; font-weight: 800; line-height: 1.25; }
.spt-step p { color: #cbd5e1; font-size: 0.95rem; line-height: 1.6; }
.spt-chip { border: 1px solid #334155; border-radius: 5px; background: #111c2e; padding: 0.15rem 0.5rem; color: #e2e8f0; font-family: "Space Grotesk", Inter, ui-sans-serif, system-ui, sans-serif; font-size: 0.8rem; }
.spt-branch { margin-top: 0.25rem; border-left: 2px solid #f1c95e; padding-left: 0.6rem; font-size: 0.88rem !important; }
.spt-branch b { color: #f1c95e; font-weight: 700; }
.spt-note { margin-top: 2rem; color: #94a3b8; font-size: 0.85rem; line-height: 1.55; }

.is-run .spt-line i { animation: spt-draw calc(var(--n) * 0.45s + 0.2s) cubic-bezier(0.45, 0, 0.55, 1) 0.1s both; }
.is-run .spt-line b { animation: spt-pulse 1.6s ease-in-out calc(var(--n) * 0.45s + 0.5s) 2 both; }
.is-run .spt-step { animation: spt-rise 0.55s cubic-bezier(0.33, 1, 0.68, 1) var(--d) both; }
.is-run .spt-dot { animation: spt-lit 0.5s ease var(--d) both; }
.is-armed * { animation-play-state: paused !important; }
@keyframes spt-draw { from { transform: scaleX(0); } to { transform: none; } }
@keyframes spt-pulse { 0% { transform: none; opacity: 0; } 10% { opacity: 1; } 90% { opacity: 1; } 100% { transform: translateX(calc(100% - 8px)); opacity: 0; } }
@keyframes spt-rise { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
@keyframes spt-lit { from { border-color: #1e293b; background: #0b1220; color: #475569; } }

/* Tablets and phones: top to bottom, the line down the left through the dots. */
@media (max-width: 1023px) {
  .spt { padding: 4.5rem 0; }
  .spt-map { margin-top: 2.5rem; }
  .spt-steps { grid-template-columns: 1fr; gap: 1.75rem; }
  .spt-step { grid-template-columns: 44px 1fr; column-gap: 1rem; row-gap: 0.3rem; }
  .spt-step > :not(.spt-dot) { grid-column: 2; }
  .spt-dot { grid-row: 1 / span 6; margin: 0; }
  .spt-line { top: 22px; bottom: 22px; left: 21px; right: auto; width: 2px; height: auto; }
  .spt-line i { transform-origin: center top; }
  .spt-line b { display: none; }
  @keyframes spt-draw { from { transform: scaleY(0); } to { transform: none; } }
}
@media (max-width: 639px) {
  .spt-shell { width: min(100% - 2rem, 84rem); }
}
@media (prefers-reduced-motion: reduce) { .spt-map * { animation: none !important; } }
</style>
